/**
 * component-quick-view.js -- Quick View Modal Web Component for Mirai Online (Phase 5)
 *
 * <quick-view-modal> custom element (desktop hover-reveal preview):
 *  - Binds every [data-quick-view-trigger] (on product cards) → dispatches/handles
 *    'quick-view:open' carrying { productUrl, productHandle, triggerElement }
 *  - open(): shows the PRE-RENDERED modal shell (never injects a new fixed element — CLS-safe,
 *    Pitfall 4), locks body scroll, sets inert on #MainContent + #header-group, traps focus
 *  - _fetchContent(): one fetch per user open to
 *    `${window.Theme.routes.root}products/{handle}?section_id=quick-view-modal` → RAW HTML
 *    (.text(), NOT sections= JSON — Pitfall 1). innerHTML inject (Shopify Liquid-rendered,
 *    same trust model as cart drawer — T-05-05 accepted). On inject, the reused
 *    <variant-selector> / <add-to-cart-form> connectedCallback fires automatically.
 *  - close(): reverses inert/scroll/focus, returns focus to the trigger element
 *  - ESC / overlay click / close button close
 *  - Touch gating (Pitfall 3 / D-10): on touch devices, every trigger gets tabindex="-1" +
 *    pointer-events:none so taps fall through to the product card link → PDP
 *  - Theme-editor compatible via shopify:section:load reinit (Open Question 1)
 *  - Light DOM only (no Shadow DOM). Registration guarded with customElements.get(...).
 *
 * Add to Cart success path is handled entirely by the REUSED <add-to-cart-form>
 * (component-product-form.js) → window.CartDrawerHelpers.openCartDrawerWithContent.
 * This component closes itself when the cart drawer opens so the drawer is unobstructed.
 */

class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundTriggerClick = this._handleTriggerClick.bind(this);
    this._boundOpenEvent = this._handleOpenEvent.bind(this);
    this._boundCartOpen = this._handleCartOpen.bind(this);
    this._isOpen = false;
    this._triggerElement = null;
    this._releaseFocusTrap = null;
    this._currentHandle = null;
  }

  connectedCallback() {
    this._init();
    // Listen on `document` — the modal shell lives in theme.liquid, but the relevant
    // section reload in the editor is the header or predictive-search section, not us.
    document.addEventListener('shopify:section:load', this._boundSectionLoad = (e) => {
      const id = e.detail && e.detail.sectionId;
      if (id && ['predictive-search', 'header'].some((s) => String(id).includes(s))) {
        this._reinit();
      }
    });
  }

  disconnectedCallback() {
    this._teardown();
    if (this._boundSectionLoad) {
      document.removeEventListener('shopify:section:load', this._boundSectionLoad);
    }
  }

  _init() {
    this._dialog = this.querySelector('[data-quick-view-modal]');
    this._overlay = this.querySelector('[data-quick-view-overlay]');
    this._closeBtn = this.querySelector('[data-quick-view-close]');
    this._contentEl = this.querySelector('[data-quick-view-content]');
    this._skeleton = this.querySelector('[data-quick-view-skeleton]');

    // Quick View triggers are bound by DELEGATION, not by snapshotting the node
    // list. Cards are injected after load (AJAX filter/sort, "Load more"), and a
    // one-shot querySelectorAll left every one of those cards' Quick View buttons
    // dead. Touch gating is done in CSS (@media (hover: none)) for the same
    // reason — per-node inline styles never reach cards that don't exist yet.
    this._isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    document.addEventListener('click', this._boundTriggerClick);

    if (this._closeBtn) this._closeBtn.addEventListener('click', this._boundClose = () => this.close());
    if (this._overlay) {
      this._overlay.addEventListener('click', this._boundOverlayClick = () => this.close());
    }
    document.addEventListener('keydown', this._boundKeydown);
    document.addEventListener('quick-view:open', this._boundOpenEvent);
    // Close ourselves when the cart drawer opens (post Add-to-Cart) so it is unobstructed.
    document.addEventListener('cart:open', this._boundCartOpen);
  }

  _teardown() {
    if (this._isOpen) this.close();
    document.removeEventListener('click', this._boundTriggerClick);
    if (this._closeBtn && this._boundClose) this._closeBtn.removeEventListener('click', this._boundClose);
    if (this._overlay && this._boundOverlayClick) this._overlay.removeEventListener('click', this._boundOverlayClick);
    document.removeEventListener('keydown', this._boundKeydown);
    document.removeEventListener('quick-view:open', this._boundOpenEvent);
    document.removeEventListener('cart:open', this._boundCartOpen);
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  _handleTriggerClick(e) {
    const btn = e.target.closest('[data-quick-view-trigger]');
    if (!btn) return;

    // On touch, taps must fall through to the card's <a> → PDP (Pitfall 3 / D-10).
    if (this._isTouch) return;

    e.preventDefault();
    document.dispatchEvent(new CustomEvent('quick-view:open', {
      bubbles: false,
      detail: {
        productUrl: btn.dataset.productUrl,
        productHandle: btn.dataset.productHandle,
        triggerElement: btn
      }
    }));
  }

  _handleOpenEvent(e) {
    const detail = e.detail || {};
    this._triggerElement = detail.triggerElement || null;
    this.open(detail.productHandle, detail.productUrl);
  }

  _handleCartOpen() {
    // The cart drawer is opening (Add to Cart succeeded). Dismiss the modal
    // underneath it, but hand off — the drawer's listener runs FIRST (its script
    // is earlier in document order) and has already set up the scroll lock,
    // inert background and focus trap that a normal close() would tear back down.
    if (this._isOpen) this.close({ handingOff: true });
  }

  open(productHandle, productUrl) {
    if (!this._dialog || this._isOpen) return;

    this._dialog.removeAttribute('hidden');
    void this._dialog.offsetWidth; // reflow so the open transition runs
    this._dialog.classList.add('quick-view-modal--open');
    this._dialog.setAttribute('aria-hidden', 'false');
    this._isOpen = true;

    window.Theme.lockScroll();
    this._setBackgroundInert(true);
    this._releaseFocusTrap = this._trapFocus(this._dialog);

    this._fetchContent(productHandle, productUrl);
  }

  /**
   * @param {Object}  [options]
   * @param {boolean} [options.handingOff] - Another overlay (the cart drawer) is
   *   taking over and has ALREADY installed its own scroll lock, inert background
   *   and focus. Dismiss the modal visually but leave that shared state alone.
   */
  close(options) {
    const opts = options || {};
    if (!this._dialog || !this._isOpen) return;
    this._dialog.classList.remove('quick-view-modal--open');
    this._dialog.setAttribute('hidden', '');
    this._dialog.setAttribute('aria-hidden', 'true');
    this._isOpen = false;

    // Always release OUR scroll lock: it is ref-counted, so when the cart drawer
    // has taken over, its own lock keeps the page locked and the count simply
    // drops 2 -> 1. Skipping this would leave the count permanently above zero
    // and the page locked forever once the drawer eventually closed.
    window.Theme.unlockScroll();

    // `inert` is NOT ref-counted — it is a plain attribute with a single owner.
    // When handing off, the drawer has already set it and will clear it on its
    // own close; clearing it here would un-inert the page under an open drawer.
    if (!opts.handingOff) {
      this._setBackgroundInert(false);
    }

    // The modal's own focus trap must always be released — it is ours, not theirs.
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
    // Clear injected content so a stale product never flashes on the next open.
    if (this._contentEl) this._contentEl.innerHTML = '';
    this._currentHandle = null;

    // Returning focus to the card behind the scrim would yank it straight out of
    // the drawer that just opened.
    if (!opts.handingOff && this._triggerElement && typeof this._triggerElement.focus === 'function') {
      this._triggerElement.focus();
    }
    this._triggerElement = null;
  }

  async _fetchContent(productHandle, productUrl) {
    this._showSkeleton(true);
    if (this._contentEl) this._contentEl.innerHTML = '';

    // Prefer an explicit handle; fall back to deriving it from the product URL.
    let handle = productHandle;
    if (!handle && productUrl) {
      const parts = productUrl.split('?')[0].split('/').filter(Boolean);
      handle = parts[parts.length - 1];
    }
    if (!handle) {
      this._showError();
      return;
    }
    this._currentHandle = handle;

    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    const base = root.endsWith('/') ? root : root + '/';
    const url = `${base}products/${handle}?section_id=quick-view-modal`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const html = await res.text(); // single section_id → RAW HTML (NOT sections= JSON)
      // Guard against a slow response landing after the user opened a different card / closed.
      if (!this._isOpen || this._currentHandle !== handle) return;
      // Safe innerHTML: Shopify Liquid-rendered server response (cart-drawer precedent, T-05-05).
      this._contentEl.innerHTML = html;
      this._showSkeleton(false);
      // <variant-selector> + <add-to-cart-form> connectedCallback fired on inject — move
      // focus to the first focusable element inside the freshly injected content.
      this._focusFirst();
    } catch (e) {
      if (this._isOpen && this._currentHandle === handle) this._showError();
    }
  }

  _showSkeleton(on) {
    if (this._skeleton) this._skeleton.hidden = !on;
  }

  _showError() {
    this._showSkeleton(false);
    if (this._contentEl) {
      this._contentEl.innerHTML =
        '<p class="quick-view__error" role="alert">' +
        'We couldn’t load this product. Please try again.' +
        '</p>';
    }
  }

  _focusFirst() {
    if (!this._dialog) return;
    const focusable = this._dialog.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') focusable.focus();
    else if (this._closeBtn) this._closeBtn.focus();
  }

  _handleKeydown(e) {
    if (e.key === 'Escape' && this._isOpen) this.close();
  }

  _setBackgroundInert(on) {
    const targets = [document.querySelector('#MainContent'), document.querySelector('#header-group')];
    targets.forEach((el) => {
      if (!el) return;
      if (on) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
  }

  _trapFocus(container) {
    const focusableSelectors =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // Re-query on every Tab — content changes after the Section Rendering inject.
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSelectors));

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    return () => container.removeEventListener('keydown', handleKeydown);
  }
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}

/* ============================================================
   QUICK VIEW GALLERY — thumbnails + swipe, synced to variant change

   The main image strip is a horizontal scroll-snap track of every product
   image. Thumbnails scroll the matching image into view; an IntersectionObserver
   syncs the active thumb back when the shopper swipes or when VariantSelector's
   existing _updateGalleryImage() scrolls the track on a variant change.
   Self-initializing (connectedCallback) so it binds on every modal inject.
   ============================================================ */

class QuickViewGallery extends HTMLElement {
  connectedCallback() {
    this._track = this.querySelector('[data-qv-gallery-track]');
    this._thumbs = Array.from(this.querySelectorAll('[data-qv-thumb]'));
    this._images = Array.from(this.querySelectorAll('[data-gallery-image]'));

    this._onThumbClick = (e) => {
      const btn = e.currentTarget;
      const index = parseInt(btn.dataset.qvThumb, 10);
      const image = this._images[index];
      if (image && typeof image.scrollIntoView === 'function') {
        image.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      this._setActive(index);
    };
    this._thumbs.forEach((btn) => btn.addEventListener('click', this._onThumbClick));

    // Sync the active thumb to whichever image is centered in the track.
    if (this._track && this._images.length > 1 && 'IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = this._images.indexOf(entry.target);
              if (index > -1) this._setActive(index);
            }
          });
        },
        { root: this._track, threshold: 0.6 }
      );
      this._images.forEach((img) => this._observer.observe(img));
    }
  }

  disconnectedCallback() {
    this._thumbs.forEach((btn) => btn.removeEventListener('click', this._onThumbClick));
    if (this._observer) this._observer.disconnect();
  }

  _setActive(index) {
    this._thumbs.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle('is-active', active);
      if (active) btn.setAttribute('aria-current', 'true');
      else btn.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('quick-view-gallery')) {
  customElements.define('quick-view-gallery', QuickViewGallery);
}

